/**
 * ParamTypesManagePage — the GLOBAL analysis-parameter-type catalogue manage screen (cross-lab, super-admin
 * only). The real fill for the shell's <ParamTypesPage> stub: a server-paginated, searchable, statut-faceted
 * table over `/management/paramtypes` with the full publish / reissue / retire / restore CRUD
 * (correct-by-reissue/supersede, never write-once — L-1).
 *
 * Conventions followed verbatim:
 *   - GATING — the super-admin gate (<useIsPlatformAdmin>) + the shell's offline + "module not mounted on
 *     this install" (isManagementAbsent → the clean cloud-only notice) states, exactly as OverviewPage.
 *   - FRAME — Screen + PageHeader + Card, the shell layout primitives. Full 5-state DoD (the Table itself
 *     supplies Loading/Error/Empty; the screen adds Offline + Forbidden + the cloud-absent notice).
 *   - DATA — the shell `useParamtypes` read hook (SERVER pagination/search/facet via the filters object) +
 *     the four screen-owned mutations. A search/filter/page change re-keys the query (no client slicing).
 *   - i18n — FR/AR via the screen messages, registered at module load. Codes/versions/disciplines stay LTR.
 */
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ForbiddenState,
  Input,
  Modal,
  OfflineState,
  Pagination,
  Select,
  Table,
  useToast,
  type Column,
  type SelectOption,
} from "@/ui";
import { ApiError } from "@/api";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { useIsPlatformAdmin } from "../session";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { DistributionBar, StatChips, type DistributionSlice } from "../components/Distribution";
import type { ParamtypeStatut } from "../types";
import type { ParamtypeDetail } from "./paramtypes.types";
import {
  isManagementAbsent,
  useParamtypes,
  useRestoreParamtype,
  useRetireParamtype,
} from "./paramtypes.api";
import { registerParamtypesMessages } from "./paramtypes.messages";
import { ParamtypeFormModal } from "./ParamtypeFormModal";

registerParamtypesMessages();

const PAGE_SIZE = 20;
const STATUT_VALUES: ReadonlyArray<ParamtypeStatut> = ["actif", "retire"];

/** A stable, accessible tone per result type for the insight meter (paired with text — never color-only). */
const TYPE_TONE: Record<string, DistributionSlice["tone"]> = {
  numerique: "info",
  qualitatif: "success",
  texte: "neutral",
};

/** Badge for a catalogue statut (actif → success · retiré → warn). Mirrors the admin pricing statut chips. */
function StatutBadge({ statut, t }: { statut: ParamtypeStatut; t: (k: string) => string }) {
  return statut === "retire" ? (
    <Badge tone="warn" glyph="⊘">
      {t("mgmt.paramtypes.statut.retire")}
    </Badge>
  ) : (
    <Badge tone="success" glyph="●">
      {t("mgmt.paramtypes.statut.actif")}
    </Badge>
  );
}

export function ParamTypesManagePage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();

  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<ParamtypeStatut | "">("");
  const [discipline, setDiscipline] = useState("");
  const [page, setPage] = useState(1);
  // undefined = form closed; null = new (publish); a row = reissue.
  const [editing, setEditing] = useState<ParamtypeDetail | null | undefined>(undefined);
  // Retire/restore confirmation target (and which action).
  const [confirm, setConfirm] = useState<{ row: ParamtypeDetail; action: "retire" | "restore" } | null>(
    null,
  );

  const enabled = isAdmin && online;

  // SERVER pagination/search/facet — the filters object re-keys the query (no client slicing).
  const filters = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(statut ? { statut } : {}),
      ...(discipline.trim() ? { discipline: discipline.trim() } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [search, statut, discipline, page],
  );
  const query = useParamtypes(filters, enabled);
  // The shell `useParamtypes` is typed `Page<ParamtypeRow>` (the tolerant shell view); the manage screen
  // binds the FULL DTO each row actually carries (the service's `toDto`). The cast narrows the shared
  // tolerant type to the authoring shape without re-declaring the read hook.
  const rows = useMemo(
    () => (query.data?.items ?? []) as unknown as ParamtypeDetail[],
    [query.data],
  );
  const total = query.data?.total ?? 0;
  const pageCount = query.data?.totalPages ?? 1;

  // Per-page catalogue synthesis (DERIVED from the loaded rows — schema-free): the result-type mix + the
  // active/retired split + the distinct-discipline count. Aggregate counts only (never echoes a code/libellé).
  const typeSlices = useMemo<DistributionSlice[]>(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.typeResultat, (counts.get(r.typeResultat) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({
        key: k,
        label: t(`mgmt.paramtypes.type.${k}`),
        value: v,
        tone: TYPE_TONE[k] ?? "neutral",
      }));
  }, [rows, t]);
  const activeCount = useMemo(() => rows.filter((r) => r.statut === "actif").length, [rows]);
  const retiredCount = rows.length - activeCount;
  const disciplineCount = useMemo(
    () => new Set(rows.map((r) => r.disciplineCode).filter(Boolean)).size,
    [rows],
  );

  const statutOptions: ReadonlyArray<SelectOption> = [
    { value: "", label: t("mgmt.paramtypes.filter.statut.all") },
    ...STATUT_VALUES.map((v) => ({ value: v, label: t(`mgmt.paramtypes.statut.${v}`) })),
  ];

  const columns: ReadonlyArray<Column<ParamtypeDetail>> = [
    {
      key: "code",
      header: t("mgmt.paramtypes.col.code"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.code,
      cell: (r) => <span className="nv-ltr-island">{r.code}</span>,
    },
    {
      key: "libelle",
      header: t("mgmt.paramtypes.col.libelle"),
      sortable: true,
      sortAccessor: (r) => (r.libelle ?? "").toLowerCase(),
      cell: (r) => r.libelle ?? "—",
    },
    {
      key: "discipline",
      header: t("mgmt.paramtypes.col.discipline"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.disciplineCode ?? "",
      cell: (r) => <span className="nv-ltr-island">{r.disciplineCode ?? "—"}</span>,
    },
    {
      key: "type",
      header: t("mgmt.paramtypes.col.type"),
      sortable: true,
      sortAccessor: (r) => r.typeResultat,
      cell: (r) => t(`mgmt.paramtypes.type.${r.typeResultat}`),
    },
    {
      key: "unite",
      header: t("mgmt.paramtypes.col.unite"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.unite ?? "—"}</span>,
    },
    {
      key: "version",
      header: t("mgmt.paramtypes.col.version"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.versionImport ?? "—"}</span>,
    },
    {
      key: "statut",
      header: t("mgmt.paramtypes.col.statut"),
      sortable: true,
      sortAccessor: (r) => r.statut,
      cell: (r) => <StatutBadge statut={r.statut} t={t} />,
    },
    {
      key: "actions",
      header: t("mgmt.paramtypes.col.actions"),
      align: "end",
      cell: (r) => (
        <div style={{ display: "inline-flex", gap: "var(--nv-s-2)", justifyContent: "flex-end" }}>
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
            {t("mgmt.paramtypes.edit")}
          </Button>
          {r.statut === "retire" ? (
            <Button size="sm" variant="ghost" onClick={() => setConfirm({ row: r, action: "restore" })}>
              {t("mgmt.paramtypes.restore")}
            </Button>
          ) : (
            <Button size="sm" variant="danger" onClick={() => setConfirm({ row: r, action: "retire" })}>
              {t("mgmt.paramtypes.retire")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const header = (
    <PageHeader
      title={t("mgmt.paramtypes.title")}
      subtitle={t("mgmt.paramtypes.subtitle")}
      actions={<Button onClick={() => setEditing(null)}>{t("mgmt.paramtypes.publish")}</Button>}
    />
  );

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />
      </Screen>
    );
  }

  // Cloud-only module on a local box → its routes 404. The clean "not available here" notice.
  if (isManagementAbsent(query.error)) {
    return (
      <Screen>
        {header}
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
            {t("mgmt.unavailable.desc")}
          </p>
        </Card>
      </Screen>
    );
  }

  const onSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };
  const onStatut = (v: string) => {
    setStatut(v as ParamtypeStatut | "");
    setPage(1);
  };
  const onDiscipline = (v: string) => {
    setDiscipline(v);
    setPage(1);
  };

  return (
    <Screen>
      {header}

      {!query.isLoading && !query.isError && rows.length > 0 && (
        <Card title={t("mgmt.paramtypes.insight.title")}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <StatChips
              chips={[
                { key: "total", label: t("mgmt.paramtypes.insight.total"), value: rows.length },
                {
                  key: "active",
                  label: t("mgmt.paramtypes.insight.active"),
                  value: activeCount,
                  tone: "success",
                },
                {
                  key: "retired",
                  label: t("mgmt.paramtypes.insight.retired"),
                  value: retiredCount,
                  tone: retiredCount > 0 ? "warn" : "neutral",
                },
                {
                  key: "disciplines",
                  label: t("mgmt.paramtypes.insight.disciplines"),
                  value: disciplineCount,
                  tone: "info",
                },
              ]}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
              <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-text-2, var(--nv-slate))" }}>
                {t("mgmt.paramtypes.insight.byType")}
              </span>
              <DistributionBar
                slices={typeSlices}
                emptyLabel={t("mgmt.paramtypes.insight.empty")}
                ariaLabel={t("mgmt.paramtypes.insight.byType.aria")}
              />
            </div>
          </div>
        </Card>
      )}

      <Card padded={false}>
        <div
          style={{
            display: "flex",
            gap: "var(--nv-s-3)",
            flexWrap: "wrap",
            padding: "var(--nv-s-3)",
            borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
          }}
        >
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t("mgmt.paramtypes.search")}
            aria-label={t("mgmt.paramtypes.search")}
            style={{ flex: "1 1 16rem" }}
          />
          <Select
            value={statut}
            onChange={(e) => onStatut(e.target.value)}
            options={statutOptions}
            aria-label={t("mgmt.paramtypes.filter.statut")}
            style={{ flex: "0 1 14rem" }}
          />
          <Input
            value={discipline}
            onChange={(e) => onDiscipline(e.target.value)}
            placeholder={t("mgmt.paramtypes.filter.discipline")}
            aria-label={t("mgmt.paramtypes.filter.discipline")}
            className="nv-ltr-island"
            style={{ flex: "0 1 12rem" }}
          />
        </div>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={rows}
            rowKey={(r) => r.code}
            caption={t("mgmt.paramtypes.title")}
            loading={query.isLoading}
            error={
              query.isError
                ? {
                    message: query.error instanceof ApiError ? query.error.message : String(query.error),
                    onRetry: () => void query.refetch(),
                  }
                : null
            }
            emptyTitle={
              search.trim() || statut || discipline.trim()
                ? t("mgmt.paramtypes.empty.search")
                : t("mgmt.paramtypes.empty.title")
            }
            {...(search.trim() || statut || discipline.trim()
              ? {}
              : { emptyDescription: t("mgmt.paramtypes.empty.desc") })}
            emptyAction={
              !search.trim() && !statut && !discipline.trim() ? (
                <Button onClick={() => setEditing(null)}>{t("mgmt.paramtypes.publish")}</Button>
              ) : undefined
            }
          />
          {/* SERVER pagination — driven by the query envelope (`total`/`totalPages`), not client slicing. */}
          {pageCount > 1 && (
            <Pagination page={page} pageCount={pageCount} total={total} onPageChange={setPage} />
          )}
        </div>
      </Card>

      <ParamtypeFormModal
        open={editing !== undefined}
        row={editing ?? null}
        onClose={() => setEditing(undefined)}
      />

      {confirm && (
        <ConfirmLifecycleModal target={confirm} onClose={() => setConfirm(null)} />
      )}
    </Screen>
  );
}

/** The retire / restore confirmation (supersede semantics — never a hard delete). */
function ConfirmLifecycleModal({
  target,
  onClose,
}: {
  target: { row: ParamtypeDetail; action: "retire" | "restore" };
  onClose: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const retire = useRetireParamtype();
  const restore = useRestoreParamtype();
  const { row, action } = target;
  const isRetire = action === "retire";
  const mutation = isRetire ? retire : restore;

  const run = () => {
    mutation.mutate(
      { code: row.code },
      {
        onSuccess: () => {
          toast.success(t(isRetire ? "mgmt.paramtypes.toast.retired" : "mgmt.paramtypes.toast.restored"));
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : t("mgmt.paramtypes.toast.error")),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(
        isRetire ? "mgmt.paramtypes.confirm.retire.title" : "mgmt.paramtypes.confirm.restore.title",
        { code: row.code },
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.paramtypes.modal.cancel")}
          </Button>
          <Button variant={isRetire ? "danger" : "primary"} onClick={run} loading={mutation.isPending}>
            {t(
              isRetire
                ? "mgmt.paramtypes.confirm.retire.submit"
                : "mgmt.paramtypes.confirm.restore.submit",
            )}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
        {t(isRetire ? "mgmt.paramtypes.confirm.retire.desc" : "mgmt.paramtypes.confirm.restore.desc")}
      </p>
    </Modal>
  );
}
